/* hook to handle the state and redirects related to the event purchase */
import { useState, useEffect, useRef, Dispatch, SetStateAction, useCallback } from 'react';
import { CardElement } from '@stripe/react-stripe-js';
import axios from 'axios';
import i18next from 'i18next';
import { StripeElements } from '@stripe/stripe-js';
import { Stripe } from '@stripe/stripe-js/types/stripe-js';
import { useLocation } from 'react-router';
import { HOME_PAGE_PAYPAL_REDIRECT } from '../../environment/environment';
import useStateRef from './useStateRef';
import useIsUserLoggedIn from './useIsUserLoggedIn';
import { purchaseEvent, purchaseEventWithoutLogin } from '../../services/EventService';
import { Ticket } from '../../models/Ticket';

interface Props {
    finalPrice: string | undefined;
    email: string;
    confirmEmail: string;
    redirect: {
        status?: boolean;
        return_url?: string;
    };
    setRedirect: Dispatch<
        SetStateAction<{
            status?: boolean;
            return_url?: string;
        }>
    >;
    event_uuid: string;
    stripe: Stripe | null;
    elements: StripeElements | null;
    onGetUser: () => void;
    usesPaypal: boolean;
    privacy: boolean;
    terms: boolean;
    ticket: Ticket;
    savedCard: {
        status?: boolean | undefined;
        stripe_payment_method_id?: number | undefined;
    };
}

interface ReturnType {
    payment: { status?: string; message?: string };
    handlePurchase: () => Promise<void>;
}

const useBuyEvent = ({
    finalPrice,
    email,
    confirmEmail,
    redirect,
    setRedirect,
    event_uuid,
    stripe,
    elements,
    onGetUser,
    usesPaypal,
    privacy,
    terms,
    ticket,
    savedCard,
}: Props): ReturnType => {
    const location = useLocation();
    const isUserLoggedIn = useIsUserLoggedIn();
    const [payment, setPayment] = useState<{ status?: string; message?: string }>({});
    const urlRef = useRef<number>(0); // used to track that the iframe url has changed, and access the url when the url belongs to gotmy
    const [paymentIntent, setPaymentIntent] = useStateRef<{ id?: string }>({}); // used for both stripe and paypal

    // Call to the API to get the status after the redirect
    const getStripeIntentStatus = useCallback(async () => {
        try {
            const status = await axios.post(`/payment/stripe_intent`, {
                payment_intent: paymentIntent.id,
            });
            if (status.data && status.data.data.status === 'succeeded') {
                setPayment({ status: 'success' });
                onGetUser();
            } else {
                setPayment({
                    status: 'error',
                    message: i18next.t('UseBuyEventComponentErrorProcessingEventPayment'),
                });
            }
        } catch (err) {}
    }, [onGetUser, paymentIntent.id]);

    /* Event listener when the iframe changes url  (load event) */
    const srcChange = useCallback((): void => {
        try {
            if (!usesPaypal && redirect && urlRef.current > 0) {
                const iframe = document.getElementById('iframe') as HTMLFrameElement;
                const redirectUrl = iframe.contentWindow ? iframe.contentWindow.location.href : '';

                if (redirectUrl.includes(redirect?.return_url || '')) {
                    setRedirect({ ...redirect, status: false });
                    // Get status!!
                    getStripeIntentStatus();
                }
            }
            urlRef.current += 1;
        } catch (e) {}
    }, [getStripeIntentStatus, redirect, setRedirect, usesPaypal]);

    useEffect(() => {
        if (redirect.status === true && !usesPaypal) {
            const iframe = document.getElementById('iframe');
            if (iframe) {
                iframe.addEventListener('load', srcChange);
            }
        }
        return () => {
            const iframe = document.getElementById('iframe');
            if (iframe) {
                iframe.removeEventListener('load', srcChange);
            }
        };
    }, [redirect.status, srcChange, usesPaypal]);

    const cardPurchaseLoggedIn = async (purchaseParam?: { stripe_token: { id: string | undefined } }) => {
        const { stripe_token } = purchaseParam || {};
        const purchaseObject =
            parseFloat(finalPrice || '') > 0
                ? {
                      event_uuid,
                      stripe_token: stripe_token && stripe_token.id ? stripe_token.id : '',
                      paypal: usesPaypal,
                      stripe_payment_method_id:
                          savedCard && savedCard.status ? savedCard.stripe_payment_method_id : '',
                      type: ticket ? 'interactive' : 'streaming',
                      remember: true,
                  }
                : { event_uuid, type: ticket ? 'interactive' : 'streaming' };

        try {
            const result = await purchaseEvent(purchaseObject);
            // @ts-ignore
            const paymentIntent = result.payment_intent;

            setPaymentIntent(paymentIntent);

            if (paymentIntent.next_action && paymentIntent.next_action.redirect_to_url) {
                setRedirect({ status: true, ...paymentIntent.next_action.redirect_to_url });
            } else if (paymentIntent.status === 'succeeded' || paymentIntent === 'succeeded') {
                setPayment({ status: 'success' });
                if (isUserLoggedIn) {
                    onGetUser();
                }
            }
        } catch (err: any) {
            const message = err.response.data.error
                .map(({ message }: { message: string }) => message)
                .join(',');
            setPayment({ status: 'error', message });
        }
    };

    const paypalRedirect = async (paymentIntent: {
        id: string;
        status: string;
        links: { href: string }[];
    }) => {
        if (paymentIntent.status === 'CREATED') {
            const storageItem = {
                token: paymentIntent.id,
                ...(email && { email }),
            };
            await localStorage.setItem('paypalIntent', JSON.stringify(storageItem));
            window.location.href = paymentIntent.links[0].href;
        }
    };

    const cardPurchaseNodLoggedIn = async (purchaseParam?: { stripe_token: { id: string | undefined } }) => {
        const { stripe_token } = purchaseParam || {};
        try {
            const purchaseObject =
                parseFloat(finalPrice || '0') > 0
                    ? {
                          event_uuid,
                          email,
                          stripe_token: stripe_token?.id,
                          paypal: usesPaypal,
                          type: ticket ? 'interactive' : 'streaming',
                      }
                    : { event_uuid, email, type: ticket ? 'interactive' : 'streaming' };

            const result = await purchaseEventWithoutLogin(purchaseObject);
            // @ts-ignore
            const paymentIntent = result.payment_intent;

            setPaymentIntent(paymentIntent);

            if (paymentIntent.next_action && paymentIntent.next_action.redirect_to_url) {
                setRedirect({ status: true, ...paymentIntent.next_action.redirect_to_url });
            } else if (paymentIntent.status === 'succeeded' || paymentIntent === 'succeeded') {
                setPayment({ status: 'success' });
            }
        } catch (err: any) {
            const message = err.response.data.error
                .map(({ message }: { message: string }) => message)
                .join(',');
            setPayment({ status: 'error', message });
        }
    };

    /* Calls to backend to get the url of paypal in logged in and new users */
    const paypalPurchase = async () => {
        let paymentIntent;
        let result;
        try {
            let purchaseObject =
                parseFloat(finalPrice || '0') > 0
                    ? {
                          type: ticket ? 'interactive' : 'streaming',
                          event_uuid,
                          paypal: usesPaypal,
                          cancel_url: `${HOME_PAGE_PAYPAL_REDIRECT}${location.pathname}?paypal_callback_error`,
                          return_url: `${HOME_PAGE_PAYPAL_REDIRECT}${location.pathname}?paypal_callback`,
                      }
                    : { event_uuid, type: ticket ? 'interactive' : 'streaming' };

            if (isUserLoggedIn) {
                result = await purchaseEvent(purchaseObject);
            } else {
                // @ts-ignore
                purchaseObject = { ...purchaseObject, email };
                result = await purchaseEventWithoutLogin(purchaseObject);
            }

            // @ts-ignore
            paymentIntent = result.payment_order;

            setPaymentIntent(paymentIntent);
            await paypalRedirect(paymentIntent);
        } catch (err: any) {
            const message = err.response.data.error
                .map(({ message }: { message: string }) => message)
                .join(',');
            setPayment({ status: 'error', message });
        }
    };

    const handlePurchase = async () => {
        if ((!privacy || !terms) && !isUserLoggedIn) {
            alert(i18next.t('UseBuyEventComponentAcceptTemsError'));
        } else if (!isUserLoggedIn && (!email || email.length === 0)) {
            alert(i18next.t('UseBuyEventComponentPleaseEnterYourEmailAddress'));
        } else if (!isUserLoggedIn && email !== confirmEmail) {
            alert(i18next.t('UseBuyEventComponentEmailsShouldMatch'));
        } else {
            setPayment({ status: 'loading' });

            if (parseInt(finalPrice || '0', 10) === 0) {
                if (!isUserLoggedIn) {
                    await cardPurchaseNodLoggedIn();
                } else {
                    await cardPurchaseLoggedIn();
                }
            } else if (!usesPaypal) {
                if (stripe && elements) {
                    let result;
                    if (!savedCard.status) {
                        const card = elements.getElement(CardElement);
                        if (card) {
                            result = await stripe.createToken(card);
                            if (result.error) {
                                alert(result.error.message);
                                setPayment({});
                            }
                        }
                    }
                    if (!isUserLoggedIn) {
                        await cardPurchaseNodLoggedIn({
                            stripe_token: result ? { id: result.token?.id } : { id: undefined },
                        });
                    } else {
                        await cardPurchaseLoggedIn({
                            stripe_token: result ? { id: result.token?.id } : { id: undefined },
                        });
                    }
                }
            } else {
                await paypalPurchase();
            }
        }
    };

    return { payment, handlePurchase };
};
export default useBuyEvent;
